/**
 * The active workout as data, and every move it makes — PURE.
 *
 * A2 kept this as a 45-field React context (`store.tsx:36-66`) whose setters
 * closed over `Date.now()`. Here each transition is a function of the session
 * and its inputs, with `now` (epoch ms) and new ids passed in, so a test pins
 * time and identity. `workout-store.ts` calls these, writes the result into
 * the persisted Zustand store and enqueues the matching outbox item; nothing
 * in this file knows either exists.
 *
 * ── A TRANSITION THAT DOES NOTHING RETURNS `null` ──────────────────────────
 * Not the same session: the caller then knows not to enqueue anything, and a
 * dead tap produces no write at all.
 *
 * ── THE SERVER'S RULES, NOT A2'S MOCK ──────────────────────────────────────
 * The targets come from `liveWorkout` (T22: the plan item, the readiness
 * adjustments, the hand-added sets); set removal is T22's `withoutPendingSet`;
 * the next set's numbers are `suggestNextSet`. A2's local `cutSets` survives
 * only as the readiness preview (`readiness-preview.ts`).
 */

import { roundRestSeconds, supersetMembersAt } from "@repo/client/plan-edit";
import {
  canRemoveSet,
  DEFAULT_REST_SECONDS,
  type LiveWorkout,
  liveWorkout,
  restTimerReducer,
  type SwapScope,
  suggestNextSet,
  withoutPendingSet,
} from "@repo/client/session";
import type { SwapReason } from "@repo/client/swap";
import type { ExerciseClip, ExerciseTracking } from "@repo/contract/catalog";
import type {
  PlanItemView,
  PlanSessionView,
  SessionAdjustment,
  SessionReadiness,
  SetKind,
  SetSide,
  SlotCandidate,
} from "@repo/contract/training";
import type { RecordFloor } from "./live-records";
import { previewAdjustments } from "./readiness-preview";

export type WorkoutPhase = "log" | "rest" | "done";
export type EndReason = "complete" | "early";
export type FeedbackState = "pending" | "sent" | "skipped";

/** A2 capped hand-added sets at three per exercise (`addSet`). */
export const MAX_EXTRA_SETS = 3;

/** Reps a plan item with no range is suggested at. */
export const DEFAULT_TARGET_REPS = 10;

/**
 * The rest after a warm-up set when the settings say nothing (`D209`, B7):
 * shorter than any working rest, because a set at 40–80 % is not what the
 * rest after a working set recovers from.
 */
export const DEFAULT_WARMUP_REST_SECONDS = 45;

/** A set's measures, as `LoggedWorkoutSet.before` keeps them. */
export interface SetBefore {
  readonly distanceMetres: number | null;
  readonly durationSeconds: number | null;
  /** `D209`; absent is a working set. Additive inside v1. */
  readonly kind?: SetKind | undefined;
  readonly load: number | null;
  readonly reps: number | null;
  readonly rpe: number | null;
  readonly side?: SetSide | undefined;
}

/**
 * One logged set.
 *
 * `id` is the client UUIDv7 the server stores the row under (plan D19) — there
 * is no separate server id, because the id IS what makes a replay idempotent.
 * A correction replaces the row with a new one (D53), so `id` moves to the
 * replacement and the ids it replaced are kept in `supersedes`, oldest first:
 * an undo that takes the set back before any of it left the device has to
 * withdraw every one of those writes.
 *
 * `load` (not A2's `kg`) so the list is T22's `LoggedSet` as it is and
 * `sessionNumbers` reads the store's `logged` directly.
 */
export interface LoggedWorkoutSet {
  /**
   * The measures this set had before its LATEST correction — what
   * `revertCorrection` puts back when the server refuses that correction.
   * Absent on a set never corrected, and on one stored before the field
   * existed (it is additive in v1, `persisted-storage.ts`).
   */
  readonly before?: SetBefore | undefined;
  readonly distanceMetres: number | null;
  readonly durationSeconds: number | null;
  /** The exercise it was recorded under: the swap's, when there was one. */
  readonly exerciseId: string;
  readonly id: string;
  /**
   * Warm-up, drop or failure (`D209`); ABSENT is a working set, as on the
   * wire. A warm-up is one of the day's sets — it fills a dot and takes a
   * rest — and counts toward no figure (`countsTowardTotals`).
   */
  readonly kind?: SetKind | undefined;
  /** `null` is bodyweight, never `0`. */
  readonly load: number | null;
  /** Epoch ms, client clock (plan D20). */
  readonly performedAt: number;
  readonly reps: number | null;
  readonly rpe: number | null;
  /** A unilateral set's side (`D209`); absent is both sides. */
  readonly side?: SetSide | undefined;
  readonly supersedes: readonly string[];
}

/** One warm-up set the calculator planned (`D209`, B2). */
export interface PlannedWarmup {
  /** Kilograms as the field logs them (per hand for `per_hand`); `null` is
   *  the empty bar's place in a ramp with no bar known. */
  readonly load: number | null;
  readonly reps: number;
}

/**
 * "Preencher abaixo" (`D209`, B4): the numbers a logged set copied into the
 * exercise's sets still to log. `second` is what the second dial holds —
 * reps, seconds or metres.
 */
export interface FillValues {
  readonly load: number | null;
  readonly second: number;
}

/** A today-only substitute (T57). The name is for rows that cannot look it up. */
export interface TodaySwap {
  /**
   * The substitute's demonstration, so the plate keeps playing after a swap
   * (T39). `null` is "this exercise has no clip"; ABSENT is "not known" — a
   * swap stored before this field existed, or one made from an id alone
   * (`swapToday(i, string)`). Both render the placeholder, and nothing
   * refetches the catalogue to fill it in.
   */
  readonly clip?: ExerciseClip | null | undefined;
  readonly exerciseId: string;
  readonly name?: string | undefined;
  /**
   * The SUBSTITUTE's own primary muscle — the plan item's is the exercise it
   * stands in for, which may work another one. Absent on a swap made without
   * it, as `clip` can be.
   */
  readonly primaryMuscleId?: number | undefined;
  /**
   * Why the person swapped, when they said (D181): it decides whether the
   * end-of-workout question is asked and what it preselects. Absent is "no
   * reason given", and so is a swap stored before the field existed.
   */
  readonly reason?: SwapReason | undefined;
  /**
   * What the SUBSTITUTE is logged as (`D151`): a timed one swapped in for a
   * counted one records its new sets as a hold, not reps. Absent reads as
   * reps and weight, as the plan item's own absent `tracks` does — a swap
   * stored before the field existed, or made from an id alone.
   */
  readonly tracks?: ExerciseTracking | undefined;
}

/**
 * One pause of the session's clock (`D245`): epoch ms it began, and ended —
 * absent while it runs. Kept as intervals, not a running total, so the
 * paused time inside ANY span can be read: an end chosen in the past
 * (the forgotten-workout question) counts only the pauses before it.
 */
export interface ClockPause {
  readonly from: number;
  readonly to?: number | undefined;
}

/** One set from the last session, per exercise id (`GET /me/exercise-history`). */
export interface PreviousSet {
  readonly load: number | null;
  readonly reps: number | null;
}

/**
 * What a quick workout (`D249`) carries beyond a plan day's session: each
 * line's swaps, from the engine's own answer, so the picker offers them
 * offline and never asks the plan's feed about an item no plan has.
 */
export interface QuickSessionMeta {
  readonly alternatives: Readonly<Record<string, readonly SlotCandidate[]>>;
}

/**
 * What a session starts on: a plan day — or, for a quick workout (`D249`),
 * the engine's lines with NO plan session, which is what keeps the plan from
 * ever moving: the end advances only a session that names one.
 */
export interface StartableSession {
  readonly items: readonly PlanItemView[];
  readonly label: PlanSessionView["label"];
  readonly sessionId: string | null;
}

export interface WorkoutSession {
  /** The readiness cut in force: the local preview until the start syncs. */
  readonly adjustments: readonly SessionAdjustment[];
  /** `true` once `adjustments` are the server's (or there is nothing to learn). */
  readonly adjustmentsSettled: boolean;
  /**
   * A blank workout (`D254`): started with no line and no plan session, its
   * exercises chosen one by one from the catalogue. It never ends itself —
   * the last open set leaves it waiting for the next exercise or the end the
   * person asks for. Absent on every other session. Additive inside v1.
   */
  readonly blank?: true | undefined;
  /**
   * "Levar os valores de hoje para o plano?" (`D247`) was answered — the plan
   * updated, or "Agora não" — so the after-view does not ask it again. Absent
   * is "not answered". Local only: the answer's plan writes are their own
   * outbox items.
   */
  readonly carried?: true | undefined;
  /**
   * The values the person answered "Sim" to (`D244`), as
   * `plausibilityKey`s: each is asked about once per workout. Absent until
   * one is. Local only.
   */
  readonly confirmed?: readonly string[] | undefined;
  /**
   * Exercise indexes put off today with "Fazer depois", oldest first — for
   * display only. Nothing is reordered: the cursor simply moved on, and the
   * walk to the next open exercise reaches them last. Absent until one is.
   */
  readonly deferred?: readonly number[] | undefined;
  /**
   * What the NEXT set is logged as, chosen on the kind sheet (`D209`, B1).
   * Absent is the default: a warm-up while planned warm-ups remain, else a
   * working set. `"working"` is "Normal" CHOSEN — the one way to log a
   * planned warm-up's place as a working set. Cleared by every set logged
   * and every cursor move.
   */
  readonly draftKind?: SetKind | "working" | undefined;
  /** The side the next set is logged on; absent is both. Cleared as above. */
  readonly draftSide?: SetSide | undefined;
  readonly ended: EndReason | null;
  /** Epoch ms, client clock. */
  readonly endedAt: number | null;
  readonly exIndex: number;
  /** Sets added (+) or taken off (−) by hand today, by exercise index. */
  readonly extra: Readonly<Record<number, number>>;
  readonly feedback: FeedbackState;
  /** "Preencher abaixo", by exercise index (`D209`, B4). Absent until used. */
  readonly fill?: Readonly<Record<number, FillValues>> | undefined;
  /** The plan session's items as they were when it started — the session
   *  must render with no network, so it carries its own copy. */
  readonly items: readonly PlanItemView[];
  /**
   * When the person answered "Continuar treinando" to the forgotten-workout
   * question (`D245`), epoch ms: it is not asked again until the workout has
   * been left alone that long once more. Absent until answered. Local only.
   */
  readonly keptOpenAt?: number | undefined;
  /** Scratch: the load the next set will be logged with. */
  readonly kg: number | null;
  readonly label: string | null;
  readonly logged: Readonly<Record<number, readonly LoggedWorkoutSet[]>>;
  /** Exercise indexes in the order sets were logged: what "Desfazer" walks. */
  readonly order: readonly number[];
  /**
   * Swapped indexes answered "never" whose plan replace landed and whose
   * exclusion did not: still unanswered, and the next asking writes the
   * exclusion alone. Kept here rather than in memory so a relaunch before
   * the retry does not lose it. Absent is "none owed". Local only.
   */
  readonly owedExclusions?: readonly number[] | undefined;
  /**
   * The clock's pauses (`D245`), oldest first; the last one open while the
   * workout is paused. Absent until the first pause. The paused time leaves
   * the duration on screen and travels with the end as `pausedSeconds`.
   */
  readonly pauses?: readonly ClockPause[] | undefined;
  readonly phase: WorkoutPhase;
  readonly planSessionId: string | null;
  readonly previous: Readonly<Record<string, readonly PreviousSet[]>>;
  /** A quick workout's own (`D249`); absent on a plan day's session. */
  readonly quick?: QuickSessionMeta | undefined;
  readonly readiness: SessionReadiness | null;
  /**
   * Each exercise's records BEFORE today (`D243`, widening `D209`'s B10
   * `bests`): what the live record compares a working set with, offline
   * included. Folded from the cached history and the sheet's cached
   * all-time records (`live-records.ts`). Absent until the history arrives.
   */
  readonly records?: Readonly<Record<string, RecordFloor>> | undefined;
  /** Scratch: the reps the next set will be logged with. */
  readonly reps: number;
  /** Epoch ms the rest lands; `null` outside a rest. */
  readonly restEndsAt: number | null;
  /** Seconds, for the ring. */
  readonly restTotal: number;
  /** Client UUIDv7, equal to the server's session id. */
  readonly sessionId: string;
  /** The set being logged — or, while resting, the set that comes next. */
  readonly setIndex: number;
  /** Epoch ms, client clock. */
  readonly startedAt: number;
  /**
   * The end-of-workout answer per swapped index: keep it for today, put it in
   * the plan, or never suggest the replaced exercise again. Absent is "not
   * answered yet". Local only — the answer's plan write is its own request.
   */
  readonly swapScopes?: Readonly<Record<number, SwapScope>> | undefined;
  readonly swaps: Readonly<Record<number, TodaySwap>>;
  /**
   * The warm-up sets the calculator inserted, by exercise index (`D209`, B2).
   * They come BEFORE the working sets: the first `warmups[i].length` sets
   * of exercise `i` are suggested from here and default to `warmup`, and
   * today's target grows by as many. Absent until one is added.
   */
  readonly warmups?:
    | Readonly<Record<number, readonly PlannedWarmup[]>>
    | undefined;
}

export type LiveSession = LiveWorkout<PlanItemView, TodaySwap>;

// ── Derivations ──────────────────────────────────────────────────────────────

export type LiveInputs = Pick<
  WorkoutSession,
  "adjustments" | "extra" | "items" | "logged" | "swaps"
> &
  Partial<Pick<WorkoutSession, "warmups">>;

/**
 * Today's targets. A planned warm-up is one more set of its exercise
 * (`D209`): it is added to the count `liveWorkout` reads beside `extra`, and
 * NOT into `extra`, which is "sets added by hand" — the summary's "N séries a
 * mais" must not count a warm-up the person asked the calculator for.
 */
export function liveOf(session: LiveInputs): LiveSession {
  const warmups = session.warmups ?? {};
  const extra: Record<number, number> = { ...session.extra };
  for (const [key, planned] of Object.entries(warmups)) {
    const i = Number(key);
    extra[i] = (extra[i] ?? 0) + planned.length;
  }
  return liveWorkout({
    adjustments: session.adjustments,
    extra,
    items: session.items,
    logged: session.logged,
    swaps: session.swaps,
  });
}

/**
 * The clip to draw at an exercise: the SUBSTITUTE's while a today-only swap
 * stands, never the plan's — the plate would otherwise play the exercise the
 * person just replaced. `null` where there is none to play, which is also
 * what a swap whose clip is unknown renders.
 *
 * The three views (`log-view`, `rest-view`, `exercise-list`) each read it, so
 * the rule lives here and not three times over.
 */
export function clipOf(
  exercise: LiveSession["exercises"][number]
): ExerciseClip | null {
  return exercise.swap === undefined
    ? exercise.item.exercise.clip
    : (exercise.swap.clip ?? null);
}

/** The exercise id sets at `i` are recorded under today. */
export function exerciseIdAt(
  session: WorkoutSession,
  i: number
): string | null {
  return session.swaps[i]?.exerciseId ?? session.items[i]?.exerciseId ?? null;
}

function isOpen(
  session: Pick<WorkoutSession, "logged">,
  live: Pick<LiveSession, "sets">,
  i: number
): boolean {
  return (session.logged[i]?.length ?? 0) < (live.sets[i] ?? 0);
}

/** The next open exercise after `from`, else the first open one, else -1. */
export function nextOpenExercise(
  session: Pick<WorkoutSession, "logged">,
  live: Pick<LiveSession, "sets">,
  from: number
): number {
  const after = live.sets.findIndex(
    (_, i) => i > from && isOpen(session, live, i)
  );
  return after >= 0
    ? after
    : live.sets.findIndex((_, i) => isOpen(session, live, i));
}

/**
 * The rest after a set at `i`: a warm-up's is the settings' warm-up rest
 * (`D209`, B7), every other set's is the item's own.
 */
function restSecondsAt(
  session: WorkoutSession,
  i: number,
  kind: SetKind | undefined,
  warmupRestSeconds: number | undefined
): number {
  if (kind === "warmup") {
    return warmupRestSeconds ?? DEFAULT_WARMUP_REST_SECONDS;
  }
  return session.items[i]?.restSeconds ?? DEFAULT_REST_SECONDS;
}

/**
 * Where a superset goes after a working set at `i` (`D246`): on to the next
 * member of the round that still has a set — straight on, with no rest — or,
 * the round over, to the rest after it and then the first member still open.
 * `next` is `-1` when every member is done. `null` when `i` stands in no
 * superset, and the workout moves as it always has.
 *
 * The round is read from the session's own copy of the day (`items`), in its
 * order, through the same run rule the plan draws with, so a group the plan
 * shows is the group the workout runs — offline included.
 */
export function roundAfter(
  session: Pick<WorkoutSession, "items" | "logged">,
  live: Pick<LiveSession, "sets">,
  i: number
): {
  readonly members: readonly number[];
  readonly next: number;
  readonly rest: boolean;
} | null {
  const members = supersetMembersAt(session.items, i);
  if (members === null) {
    return null;
  }
  const onward = members.find((j) => j > i && isOpen(session, live, j));
  if (onward !== undefined) {
    return { members, next: onward, rest: false };
  }
  const first = members.find((j) => isOpen(session, live, j));
  return { members, next: first ?? -1, rest: true };
}

/** {@link roundAfter} for a working set; a warm-up has no round. */
function workingRoundAfter(
  session: Pick<WorkoutSession, "items" | "logged">,
  live: Pick<LiveSession, "sets">,
  i: number,
  kind: SetKind | undefined
): ReturnType<typeof roundAfter> {
  return kind === "warmup" ? null : roundAfter(session, live, i);
}

/**
 * The rest after a superset's round (`D246`): the longest rest any member
 * prescribes, so the heaviest lift of the round gets its own.
 */
function roundRestAt(
  session: Pick<WorkoutSession, "items">,
  members: readonly number[]
): number {
  return (
    roundRestSeconds(
      members.map((j) => session.items[j]?.restSeconds ?? null)
    ) ?? DEFAULT_REST_SECONDS
  );
}

/** The logged sets of `i` that count (`D209`): every one but a warm-up. */
function workingSetsAt(
  session: Pick<WorkoutSession, "logged">,
  i: number
): readonly LoggedWorkoutSet[] {
  return (session.logged[i] ?? []).filter((set) => set.kind !== "warmup");
}

/**
 * The planned warm-up the NEXT set of `i` is, or `null` (`D209`). The warm-ups
 * come first, so the next set is a warm-up while fewer sets than planned
 * warm-ups are logged.
 */
export function plannedWarmupAt(
  session: Pick<WorkoutSession, "logged" | "warmups">,
  i: number
): PlannedWarmup | null {
  const planned = session.warmups?.[i] ?? [];
  return planned[session.logged[i]?.length ?? 0] ?? null;
}

/**
 * What the next set of `i` is logged as unless the person says otherwise:
 * the kind sheet's choice for the exercise under the cursor, else a warm-up
 * while one is planned, else a working set (absent).
 */
export function nextKindAt(
  session: Pick<WorkoutSession, "draftKind" | "exIndex" | "logged" | "warmups">,
  i: number
): SetKind | undefined {
  if (i === session.exIndex && session.draftKind !== undefined) {
    return session.draftKind === "working" ? undefined : session.draftKind;
  }
  return plannedWarmupAt(session, i) === null ? undefined : "warmup";
}

/**
 * What the next set at exercise `i` is pre-filled with.
 *
 * - A set already logged here today: `suggestNextSet` from it, stepping by the
 *   item's `loadStepKg` when the plan has one (contracts §4).
 * - Otherwise the prescription: the item's `targetLoad`, falling back to the
 *   same set of the last session and then its first set, at the top of the
 *   rep range. A swap has no `targetLoad` of its own (the load belongs to the
 *   exercise it was written for), so a swapped exercise starts from history.
 *
 * Two `D209` rules sit in front. A planned warm-up is suggested as planned
 * (B2), and "Preencher abaixo" (B4) is what every remaining working set of
 * the exercise is suggested at. And the working sets are counted WITHOUT the
 * warm-ups: the third set after two warm-ups is the first working set, it is
 * compared with the first set of last time, and a warm-up is never the
 * "last set" the next one steps from.
 */
export function suggestionAt(
  session: WorkoutSession,
  i: number
): { readonly kg: number | null; readonly reps: number } {
  const item = session.items[i];
  if (item === undefined) {
    return { kg: null, reps: 0 };
  }
  const warmup = plannedWarmupAt(session, i);
  if (warmup !== null) {
    return { kg: warmup.load, reps: warmup.reps };
  }
  const fill = session.fill?.[i];
  if (fill !== undefined) {
    return { kg: fill.load, reps: fill.second };
  }
  const exerciseId = exerciseIdAt(session, i) ?? item.exerciseId;
  const swapped = session.swaps[i] !== undefined;
  const logged = workingSetsAt(session, i);
  const history = session.previous[exerciseId] ?? [];
  const repsMin =
    item.targetRepsMin ?? item.targetRepsMax ?? DEFAULT_TARGET_REPS;
  const repsMax = Math.max(repsMin, item.targetRepsMax ?? repsMin);
  const prior = history[logged.length] ?? history[0];
  const planned = swapped ? null : item.targetLoad;
  const load = planned ?? prior?.load ?? null;
  const last = logged.at(-1);
  const suggestion = suggestNextSet({
    last:
      last === undefined
        ? null
        : { load: last.load, reps: last.reps, rpe: last.rpe },
    ...(item.loadStepKg === undefined ? {} : { stepKg: item.loadStepKg }),
    targets: { load, repsMax, repsMin },
  });
  return { kg: suggestion.load, reps: suggestion.reps };
}

/**
 * What the first WORKING set of `i` would be suggested at, whatever warm-ups
 * are planned — the load the warm-up calculator ramps to (`D209`, B2).
 */
export function workingSuggestionAt(
  session: WorkoutSession,
  i: number
): { readonly kg: number | null; readonly reps: number } {
  const warmups = { ...session.warmups };
  delete warmups[i];
  return suggestionAt({ ...session, warmups }, i);
}

/** The last set logged, and where. */
export function lastLogged(
  session: WorkoutSession
): { readonly exIndex: number; readonly set: LoggedWorkoutSet } | null {
  const i = session.order.at(-1);
  if (i === undefined) {
    return null;
  }
  const set = session.logged[i]?.at(-1);
  return set === undefined ? null : { exIndex: i, set };
}

export function findLogged(
  session: WorkoutSession,
  id: string
): { readonly exIndex: number; readonly index: number } | null {
  for (const [key, sets] of Object.entries(session.logged)) {
    const index = sets.findIndex((set) => set.id === id);
    if (index >= 0) {
      return { exIndex: Number(key), index };
    }
  }
  return null;
}

interface LocatedSet {
  readonly exIndex: number;
  readonly index: number;
  readonly set: LoggedWorkoutSet;
}

/**
 * The set an id names: the one carrying it, or the one that SUPERSEDED it.
 * A refused write names the id it was queued under, and the set on screen may
 * have been corrected since, which moved its `id` to the replacement (D53).
 */
function locateSet(session: WorkoutSession, setId: string): LocatedSet | null {
  for (const [key, sets] of Object.entries(session.logged)) {
    const index = sets.findIndex(
      (candidate) =>
        candidate.id === setId || candidate.supersedes.includes(setId)
    );
    const set = index < 0 ? undefined : sets[index];
    if (set !== undefined) {
      return { exIndex: Number(key), index, set };
    }
  }
  return null;
}

/** {@link locateSet}, for a caller that only wants the set itself. */
export function loggedSetFor(
  session: WorkoutSession,
  setId: string
): LoggedWorkoutSet | null {
  return locateSet(session, setId)?.set ?? null;
}

/** The remaining rest at `now`, through T22's reducer (never a counter). */
export function restRemainingMs(session: WorkoutSession, now: number): number {
  if (session.phase !== "rest" || session.restEndsAt === null) {
    return 0;
  }
  return restTimerReducer(
    {
      endsAtMs: session.restEndsAt,
      finished: false,
      remainingMs: session.restEndsAt - now,
      running: true,
    },
    { now, type: "tick" }
  ).remainingMs;
}

// ── Transitions ──────────────────────────────────────────────────────────────

/**
 * A new session on a plan session's items. The readiness cut is the local
 * preview until the server's answer replaces it; with no readiness there is
 * nothing to learn from the server and the (empty) list is already final.
 *
 * A quick workout (`D249`, `meta.quick`) starts SETTLED with no cut: the
 * engine composed its lines with the check-in already applied, and the
 * server, with no plan items to cut, answers an empty list anyway — a
 * preview here would cut the same sets twice.
 */
export function startSession(
  planSession: StartableSession,
  readiness: SessionReadiness | null,
  meta: {
    /** A blank workout (`D254`): no line yet, and no end of its own. */
    readonly blank?: boolean | undefined;
    readonly id: string;
    readonly now: number;
    readonly quick?: QuickSessionMeta | undefined;
  }
): WorkoutSession {
  const { quick } = meta;
  const base: WorkoutSession = {
    ...(meta.blank === true ? { blank: true as const } : {}),
    adjustments:
      quick === undefined
        ? previewAdjustments(planSession.items, readiness)
        : [],
    adjustmentsSettled: quick !== undefined || readiness === null,
    ended: null,
    endedAt: null,
    exIndex: 0,
    extra: {},
    feedback: "pending",
    items: planSession.items,
    kg: null,
    label: planSession.label,
    logged: {},
    order: [],
    phase: "log",
    planSessionId: planSession.sessionId,
    previous: {},
    ...(quick === undefined ? {} : { quick }),
    readiness,
    reps: 0,
    restEndsAt: null,
    restTotal: 0,
    sessionId: meta.id,
    setIndex: 0,
    startedAt: meta.now,
    swaps: {},
  };
  return { ...base, ...suggestionAt(base, 0) };
}

/**
 * Moves the cursor to exercise `i` and pre-fills its next set. The kind
 * sheet's choice belonged to the set it was made for, so it goes (`D209`).
 */
function cursorAt(
  session: WorkoutSession,
  i: number
): Pick<
  WorkoutSession,
  "draftKind" | "draftSide" | "exIndex" | "kg" | "reps" | "setIndex"
> {
  return {
    draftKind: undefined,
    draftSide: undefined,
    exIndex: i,
    setIndex: session.logged[i]?.length ?? 0,
    ...suggestionAt({ ...session, exIndex: i }, i),
  };
}

export interface SetMeasures {
  readonly distanceMetres?: number | null;
  readonly durationSeconds?: number | null;
  /** Overrides {@link nextKindAt}; `null` is a working set (`D209`). */
  readonly kind?: SetKind | null;
  /** Overrides the scratch `kg`. */
  readonly load?: number | null;
  /** Overrides the scratch `reps`; `null` for a hold or a carry. */
  readonly reps?: number | null;
  readonly rpe?: number | null;
  /** Overrides the draft side; `null` is both sides (`D209`). */
  readonly side?: SetSide | null;
}

export interface LoggedTransition {
  /** No exercise is left open: the session ended as `complete`. */
  readonly completed: boolean;
  readonly session: WorkoutSession;
  readonly set: LoggedWorkoutSet;
}

/** A2's `logSet`: the set is logged, then a rest towards the next open set. */
export function logSet(
  session: WorkoutSession,
  measures: SetMeasures,
  meta: {
    readonly id: string;
    readonly now: number;
    /** The settings' warm-up rest (`D209`, B7); absent is the default. */
    readonly warmupRestSeconds?: number;
  }
): LoggedTransition | null {
  if (session.ended !== null || session.phase !== "log") {
    return null;
  }
  const i = session.exIndex;
  const exerciseId = exerciseIdAt(session, i);
  if (exerciseId === null) {
    return null;
  }
  // A set is the person back at it: a paused clock runs again (`D245`).
  const pauses = closedPauses(session.pauses, meta.now);
  const set = setFrom(session, measures, exerciseId, meta);
  const { kind } = set;
  const done = [...(session.logged[i] ?? []), set];
  const logged = { ...session.logged, [i]: done };
  const order = [...session.order, i];
  const withSet: WorkoutSession = {
    ...session,
    draftKind: undefined,
    draftSide: undefined,
    logged,
    order,
    ...(pauses === undefined ? {} : { pauses }),
  };
  const live = liveOf(withSet);
  // A warm-up is the exercise's own business: it rests and stays, superset
  // or not. A working set in a superset moves through the round (`D246`).
  const round = workingRoundAfter(withSet, live, i, kind);
  if (round !== null) {
    return roundStep(withSet, live, round, set, meta.now);
  }
  const restTotal = restSecondsAt(session, i, kind, meta.warmupRestSeconds);
  const rest = {
    phase: "rest" as const,
    restEndsAt: meta.now + restTotal * 1000,
    restTotal,
  };
  if (done.length < (live.sets[i] ?? 0)) {
    return {
      completed: false,
      session: { ...withSet, ...rest, ...cursorAt(withSet, i) },
      set,
    };
  }
  const next = nextOpenExercise(withSet, live, i);
  if (next < 0) {
    return dayDone(withSet, i, set, meta.now);
  }
  return {
    completed: false,
    session: { ...withSet, ...rest, ...cursorAt(withSet, next) },
    set,
  };
}

/**
 * The day's last open set was logged at `i`. A session ends `complete` — or,
 * a blank one (`D254`), waits on that exercise with no rest, for the next
 * exercise the person adds or the end they ask for.
 */
function dayDone(
  withSet: WorkoutSession,
  i: number,
  set: LoggedWorkoutSet,
  now: number
): LoggedTransition {
  if (withSet.blank === true) {
    return {
      completed: false,
      session: {
        ...withSet,
        ...cursorAt(withSet, i),
        phase: "log",
        restEndsAt: null,
      },
      set,
    };
  }
  return {
    completed: true,
    session: finish(withSet, "complete", now) ?? withSet,
    set,
  };
}

/**
 * The set {@link logSet} records at the cursor: the measures given, else the
 * scratch numbers; the kind sheet's choice, else {@link nextKindAt}.
 */
function setFrom(
  session: WorkoutSession,
  measures: SetMeasures,
  exerciseId: string,
  meta: { readonly id: string; readonly now: number }
): LoggedWorkoutSet {
  const kind =
    measures.kind === undefined
      ? nextKindAt(session, session.exIndex)
      : (measures.kind ?? undefined);
  const side =
    measures.side === undefined
      ? session.draftSide
      : (measures.side ?? undefined);
  return {
    distanceMetres: measures.distanceMetres ?? null,
    durationSeconds: measures.durationSeconds ?? null,
    exerciseId,
    id: meta.id,
    // Absent, never `undefined` written out: the store is JSON, and a
    // working set is stored exactly as one was before `D209`.
    ...(kind === undefined ? {} : { kind }),
    load: measures.load === undefined ? session.kg : measures.load,
    performedAt: meta.now,
    reps: measures.reps === undefined ? session.reps : measures.reps,
    rpe: measures.rpe ?? null,
    ...(side === undefined ? {} : { side }),
    supersedes: [],
  };
}

/**
 * The step after a working set of a superset (`D246`): no rest and the next
 * member while the round lasts; at its end the round's rest, towards the
 * first member still open — or, the group done, the next open exercise after
 * it, or the end.
 */
function roundStep(
  withSet: WorkoutSession,
  live: LiveSession,
  round: NonNullable<ReturnType<typeof roundAfter>>,
  set: LoggedWorkoutSet,
  now: number
): LoggedTransition {
  if (!round.rest) {
    return {
      completed: false,
      session: {
        ...withSet,
        ...cursorAt(withSet, round.next),
        phase: "log",
        restEndsAt: null,
      },
      set,
    };
  }
  const last = round.members.at(-1) ?? withSet.exIndex;
  const next =
    round.next >= 0 ? round.next : nextOpenExercise(withSet, live, last);
  if (next < 0) {
    return dayDone(withSet, withSet.exIndex, set, now);
  }
  const restTotal = roundRestAt(withSet, round.members);
  return {
    completed: false,
    session: {
      ...withSet,
      phase: "rest",
      restEndsAt: now + restTotal * 1000,
      restTotal,
      ...cursorAt(withSet, next),
    },
    set,
  };
}

/**
 * A2's `undoSet`: the last logged set comes back off, and the cursor returns
 * to it with its numbers. Allowed after the end too, as in A2: the server
 * accepts a delete in an ended session, and a set logged after it extends the
 * session's end (`workout-session.ts`), so un-ending locally is honest.
 */
export function undoSet(
  session: WorkoutSession
): { readonly session: WorkoutSession; readonly set: LoggedWorkoutSet } | null {
  const last = lastLogged(session);
  if (last === null) {
    return null;
  }
  const sets = session.logged[last.exIndex] ?? [];
  return {
    session: {
      ...session,
      // The set comes back as what it was: its kind and side return to the
      // draft, so logging it again logs the same thing (`D209`) — a working
      // set as "working" said out loud, so a planned warm-up's place it took
      // stays a working set.
      draftKind: last.set.kind ?? "working",
      draftSide: last.set.side,
      ended: null,
      endedAt: null,
      exIndex: last.exIndex,
      feedback: "pending",
      kg: last.set.load,
      logged: { ...session.logged, [last.exIndex]: sets.slice(0, -1) },
      order: session.order.slice(0, -1),
      phase: "log",
      reps: last.set.reps ?? session.reps,
      restEndsAt: null,
      setIndex: sets.length - 1,
    },
    set: last.set,
  };
}

export interface SetCorrection {
  readonly distanceMetres?: number | null;
  readonly durationSeconds?: number | null;
  /** `D209`: `null` makes it a working set again. */
  readonly kind?: SetKind | null;
  readonly load?: number | null;
  readonly reps?: number | null;
  readonly rpe?: number | null;
  /** `D209`: `null` is both sides. */
  readonly side?: SetSide | null;
}

/** A correction's value as the set stores it: `null` kind or side is absent. */
function storedValue(
  key: keyof SetCorrection,
  value: SetCorrection[keyof SetCorrection]
): unknown {
  return (key === "kind" || key === "side") && value === null
    ? undefined
    : value;
}

/**
 * A logged set's numbers, fixed. The row is replaced by a new one with a new
 * client id (D53, contracts §1 `correctSetInput.id`); only the fields given
 * change.
 */
export function correctSet(
  session: WorkoutSession,
  id: string,
  values: SetCorrection,
  meta: { readonly id: string }
): {
  readonly previousId: string;
  readonly session: WorkoutSession;
  readonly set: LoggedWorkoutSet;
} | null {
  const at = findLogged(session, id);
  if (at === null) {
    return null;
  }
  const sets = session.logged[at.exIndex] ?? [];
  const old = sets[at.index];
  if (old === undefined) {
    return null;
  }
  // A kind or side of `null` is the absent one the set already stores, so
  // "make it a working set" on a working set changes nothing (`D209`).
  const changed = (Object.keys(values) as (keyof SetCorrection)[]).filter(
    (key) =>
      values[key] !== undefined && storedValue(key, values[key]) !== old[key]
  );
  if (changed.length === 0) {
    return null;
  }
  const replaced: Record<string, unknown> = { ...old };
  for (const key of changed) {
    const value = storedValue(key, values[key]);
    if (value === undefined) {
      delete replaced[key];
    } else {
      replaced[key] = value;
    }
  }
  const set: LoggedWorkoutSet = {
    ...(replaced as unknown as LoggedWorkoutSet),
    // What `revertCorrection` puts back if the server refuses this write.
    // Only one level is kept: a second correction overwrites it, and only one
    // correction of a set can be quarantined at a time anyway, because a
    // refused write holds everything queued behind it (D23).
    before: {
      distanceMetres: old.distanceMetres,
      durationSeconds: old.durationSeconds,
      ...(old.kind === undefined ? {} : { kind: old.kind }),
      load: old.load,
      reps: old.reps,
      rpe: old.rpe,
      ...(old.side === undefined ? {} : { side: old.side }),
    },
    id: meta.id,
    supersedes: [...old.supersedes, old.id],
  };
  return {
    previousId: old.id,
    session: {
      ...session,
      logged: {
        ...session.logged,
        [at.exIndex]: sets.map((s, j) => (j === at.index ? set : s)),
      },
    },
    set,
  };
}

/**
 * The `order` entry of `logged[i][k]`: the k-th occurrence of `i`, never the
 * last one. `order` is what "Desfazer" walks, so dropping the wrong
 * occurrence would send a later undo to a different exercise.
 */
function withoutOrderEntry(
  order: readonly number[],
  i: number,
  k: number
): readonly number[] {
  let seen = 0;
  for (const [at, value] of order.entries()) {
    if (value !== i) {
      continue;
    }
    if (seen === k) {
      return [...order.slice(0, at), ...order.slice(at + 1)];
    }
    seen += 1;
  }
  return order;
}

/**
 * A set the server REFUSED comes off the workout: the quarantine sheet's
 * "Descartar" (D23, T23). Not an undo — nothing goes back to the queue here,
 * because the write the person discarded is the one being withdrawn — and
 * `setId` may be an id this set SUPERSEDED, since a refused record holds its
 * session's stream while the set itself can have been corrected meanwhile.
 *
 * An ended session STAYS ended: its end is already queued or sent, and the
 * server raises `endedAt` to the last visible set anyway, so re-opening the
 * session here would only disagree with what it stores.
 */
export function forgetSet(
  session: WorkoutSession,
  setId: string
): WorkoutSession | null {
  const at = locateSet(session, setId);
  if (at === null) {
    return null;
  }
  const sets = session.logged[at.exIndex] ?? [];
  const next: WorkoutSession = {
    ...session,
    logged: {
      ...session.logged,
      [at.exIndex]: sets.filter((_, j) => j !== at.index),
    },
    order: withoutOrderEntry(session.order, at.exIndex, at.index),
  };
  return session.exIndex === at.exIndex && session.phase === "log"
    ? { ...next, ...cursorAt(next, at.exIndex) }
    : next;
}

/**
 * A correction the server refused, taken back: the set returns to the row it
 * replaced — the id the server actually HOLDS, so a later undo deletes
 * something that exists — and to the numbers `correctSet` kept in `before`.
 * `null` when nothing carries `replacementId`, or it superseded nothing.
 */
export function revertCorrection(
  session: WorkoutSession,
  replacementId: string
): WorkoutSession | null {
  const at = findLogged(session, replacementId);
  if (at === null) {
    return null;
  }
  const sets = session.logged[at.exIndex] ?? [];
  const set = sets[at.index];
  const previousId = set?.supersedes.at(-1);
  if (set === undefined || previousId === undefined) {
    return null;
  }
  const reverted: LoggedWorkoutSet = {
    ...set,
    // The kind and side are put back too (`D209`): absent in `before` is a
    // working set on both sides, so the corrected ones must not survive it.
    // A set stored before `before` existed keeps them, with its numbers.
    ...(set.before === undefined
      ? {}
      : { kind: set.before.kind, side: set.before.side }),
    // A set stored before `before` existed reverts its id alone: the numbers
    // on screen are then the corrected ones, which is the lesser wrong.
    ...set.before,
    before: undefined,
    id: previousId,
    supersedes: set.supersedes.slice(0, -1),
  };
  return {
    ...session,
    logged: {
      ...session.logged,
      [at.exIndex]: sets.map((s, j) => (j === at.index ? reverted : s)),
    },
  };
}

/** A set as the server holds it (`GET /me/sessions/:id/sets`), times in ms. */
export type ServerSet = Omit<LoggedWorkoutSet, "before" | "supersedes">;

/**
 * The server's sets taken in: every row this session does not know (T-188).
 *
 * On Android a Preferences write resolves before it reaches the disk, and the
 * outbox sends as soon as its own write resolves — so a kill in that window
 * leaves a set on the server that neither this session nor the queue
 * remembers. The server's row carries the client's own id (D19), so an id
 * this session never saw was still logged HERE.
 *
 * ADOPT, NEVER DELETE. An unknown row is appended at its exercise; a row
 * whose exercise the session cannot place is left out. A set this session
 * holds and the server does not list is KEPT: with the queue empty it can
 * only be a set whose record was lost before it was sent (the session reached
 * the disk, the queue behind it did not), and removing it would add a loss on
 * the screen to a loss on the server. The price is a correction lost the same
 * way (D53: a new row id, the old row gone), which shows as the old set AND
 * the new one — visible, and undoable, rather than silent.
 *
 * The cursor moves only where its exercise gained a set, and the day's last
 * open set ends the session as `logSet` would.
 *
 * Only for a session whose writes have all left the queue: a pending delete
 * or correction would otherwise read as a set to take back in.
 */
export function adoptServerSets(
  session: WorkoutSession,
  server: readonly ServerSet[],
  now: number
): { readonly completed: boolean; readonly session: WorkoutSession } | null {
  if (session.ended !== null) {
    return null;
  }
  const known = new Set(
    Object.values(session.logged).flatMap((sets) =>
      sets.flatMap((set) => [set.id, ...set.supersedes])
    )
  );
  const unknown = server
    .filter((set) => !known.has(set.id))
    .sort((a, b) => a.performedAt - b.performedAt);
  const logged: Record<number, readonly LoggedWorkoutSet[]> = {
    ...session.logged,
  };
  const order = [...session.order];
  let changed = false;
  for (const set of unknown) {
    // The exercise it was recorded under; with the same exercise twice in a
    // day, the first one still open.
    const at = session.items
      .map((_, i) => i)
      .filter((i) => exerciseIdAt(session, i) === set.exerciseId);
    const targets = liveOf({ ...session, logged });
    const place = at.find((i) => isOpen({ logged }, targets, i)) ?? at[0];
    if (place === undefined) {
      continue;
    }
    logged[place] = [...(logged[place] ?? []), { ...set, supersedes: [] }];
    order.push(place);
    changed = true;
  }
  if (!changed) {
    return null;
  }
  const next: WorkoutSession = { ...session, logged, order };
  const here = session.exIndex;
  if ((logged[here]?.length ?? 0) === (session.logged[here]?.length ?? 0)) {
    return { completed: false, session: next };
  }
  const live = liveOf(next);
  const to = isOpen(next, live, here)
    ? here
    : nextOpenExercise(next, live, here);
  if (to < 0 && session.blank === true) {
    return { completed: false, session: next };
  }
  if (to < 0) {
    return {
      completed: true,
      session: finish(next, "complete", now) ?? next,
    };
  }
  return { completed: false, session: { ...next, ...cursorAt(next, to) } };
}

/** A session another device started and the server still holds open (T09). */
export interface RemoteSession {
  readonly readiness: SessionReadiness | null;
  /** The other device's client id, which is the server's session id (D163). */
  readonly sessionId: string;
  /** Its visible sets, as `GET /me/sessions` lists them. */
  readonly sets: readonly ServerSet[];
  /** Epoch ms, the other device's clock (D163). */
  readonly startedAt: number;
}

/**
 * "Continuar esse treino" (T09): the open session rebuilt HERE, on the plan
 * session it was started from.
 *
 * It is a start under the other device's id and time, followed by the T-188
 * adoption of its sets — the same two steps that give a session started here
 * the sets the server holds for it, so the two cannot drift. Nothing about it
 * is a new start: the server has the session already, and the caller queues
 * no `session.start`. With a readiness the cut is the local preview until the
 * adjustments sync replays the start under the same id, which the server
 * answers from what it stored (D163).
 *
 * `completed` is the adoption's: every set of the day was already logged, so
 * the session ended here as `complete` and its end is the caller's to queue.
 */
export function resumeSession(
  planSession: Pick<PlanSessionView, "items" | "label" | "sessionId">,
  remote: RemoteSession,
  now: number
): { readonly completed: boolean; readonly session: WorkoutSession } {
  const started = startSession(planSession, remote.readiness, {
    id: remote.sessionId,
    now: remote.startedAt,
  });
  return (
    adoptServerSets(started, remote.sets, now) ?? {
      completed: false,
      session: started,
    }
  );
}

/** One set more at `i`, today only (A2 caps it at three). */
export function addSet(
  session: WorkoutSession,
  i: number
): WorkoutSession | null {
  if (session.ended !== null || session.items[i] === undefined) {
    return null;
  }
  const extra = session.extra[i] ?? 0;
  if (extra >= MAX_EXTRA_SETS) {
    return null;
  }
  return { ...session, extra: { ...session.extra, [i]: extra + 1 } };
}

/** What the removal toast's "Desfazer" hands back (A2 store.tsx:503-519). */
export interface RemovedSet {
  readonly after: WorkoutSession;
  readonly before: WorkoutSession;
  /** The removal was the day's last open set: the session ended complete. */
  readonly completed: boolean;
  readonly exIndex: number;
}

/**
 * One PENDING set of exercise `i` comes off today's target (T22's
 * `withoutPendingSet`, a2-port-map §5). Never a logged one, never below the
 * sets done, never below one. On the current exercise while resting the
 * cursor moves on, and the day's last open set ends the session. On another
 * exercise only its target moves.
 */
export function removePendingSet(
  session: WorkoutSession,
  i: number,
  now: number
): RemovedSet | null {
  if (session.ended !== null || session.items[i] === undefined) {
    return null;
  }
  const live = liveOf(session);
  if (i !== session.exIndex) {
    const probe = { ...session, exIndex: i, phase: "log" as const };
    const removed = withoutPendingSet(probe, live);
    if (removed.state === probe) {
      return null;
    }
    const after = { ...session, extra: removed.state.extra };
    return { after, before: session, completed: false, exIndex: i };
  }
  const removed = withoutPendingSet(session, live);
  if (removed.state === session) {
    return null;
  }
  let after: WorkoutSession = removed.state;
  const completed = removed.completed && session.blank !== true;
  if (completed) {
    after = finish(after, "complete", now) ?? after;
  } else if (after.exIndex !== session.exIndex) {
    after = { ...after, ...cursorAt(after, after.exIndex) };
  }
  return { after, before: session, completed, exIndex: i };
}

/**
 * The toast's "Desfazer". Nothing happened since the removal: everything goes
 * back, cursor included. Something did (a set was logged): only the set comes
 * back, without rewinding what came after it.
 */
export function restoreRemovedSet(
  session: WorkoutSession,
  snapshot: RemovedSet
): WorkoutSession | null {
  if (session.sessionId !== snapshot.before.sessionId) {
    return null;
  }
  if (session === snapshot.after) {
    return snapshot.before;
  }
  const i = snapshot.exIndex;
  return {
    ...session,
    extra: { ...session.extra, [i]: (session.extra[i] ?? 0) + 1 },
  };
}

export function canRemoveAt(session: WorkoutSession, i: number): boolean {
  if (session.ended !== null) {
    return false;
  }
  const live = liveOf(session);
  if (i === session.exIndex) {
    return canRemoveSet(session, live);
  }
  return canRemoveSet({ ...session, exIndex: i, phase: "log" }, live);
}

/** Rest over, by a skip or by reaching zero: back to logging. */
export function endRest(session: WorkoutSession): WorkoutSession | null {
  if (session.phase !== "rest") {
    return null;
  }
  return { ...session, phase: "log", restEndsAt: null };
}

/** ±seconds on the running rest; past zero it is over (T22's reducer). */
export function adjustRest(
  session: WorkoutSession,
  deltaSeconds: number,
  now: number
): WorkoutSession | null {
  if (session.phase !== "rest" || session.restEndsAt === null) {
    return null;
  }
  const next = restTimerReducer(
    {
      endsAtMs: session.restEndsAt,
      finished: false,
      remainingMs: Math.max(0, session.restEndsAt - now),
      running: true,
    },
    { deltaSeconds, now, type: "adjust" }
  );
  if (!next.running || next.endsAtMs === null) {
    return endRest(session);
  }
  return {
    ...session,
    restEndsAt: next.endsAtMs,
    restTotal: Math.max(0, session.restTotal + deltaSeconds),
  };
}

/** Straight to exercise `i`, logging. Any rest is dropped. */
export function jumpTo(
  session: WorkoutSession,
  i: number
): WorkoutSession | null {
  if (session.ended !== null || session.items[i] === undefined) {
    return null;
  }
  return {
    ...session,
    ...cursorAt(session, i),
    phase: "log",
    restEndsAt: null,
  };
}

/**
 * "Fazer depois": the cursor goes to the next open exercise and the current
 * one is noted in `deferred`. The rest keeps running — unlike `jumpTo`, this
 * is not the person choosing to log now, only choosing what comes after the
 * rest. Nothing moves in `items`: everything keys on the index, and
 * `nextOpenExercise` walks forward and wraps, so the deferred exercise comes
 * back once everything after it is done.
 *
 * `null` when the session ended, the current exercise has nothing left to
 * defer, or no OTHER exercise is open.
 */
export function deferCurrent(
  session: WorkoutSession,
  live: Pick<LiveSession, "sets">
): WorkoutSession | null {
  const i = session.exIndex;
  if (session.ended !== null || !isOpen(session, live, i)) {
    return null;
  }
  const next = nextOpenExercise(session, live, i);
  if (next < 0 || next === i) {
    return null;
  }
  const deferred = session.deferred ?? [];
  return {
    ...session,
    ...cursorAt(session, next),
    deferred: deferred.includes(i) ? deferred : [...deferred, i],
  };
}

/**
 * The end-of-workout answer for the swap at `i`. Taken on an ENDED session,
 * unlike a swap: the question is asked on the summary, after the end. An
 * answer settles an exclusion the swap owed, whichever answer it is.
 * `null` where there is no swap to answer for, or the answer is already this.
 */
export function setSwapScope(
  session: WorkoutSession,
  i: number,
  scope: SwapScope
): WorkoutSession | null {
  if (session.swaps[i] === undefined || session.swapScopes?.[i] === scope) {
    return null;
  }
  return withoutOwed(
    { ...session, swapScopes: { ...session.swapScopes, [i]: scope } },
    i
  );
}

/**
 * The swap at `i` answered "never", its plan replace landed and its exclusion
 * did not: the exclusion is owed until the question is answered again. `null`
 * where there is no unanswered swap at `i`, or it is already owed.
 */
export function oweExclusion(
  session: WorkoutSession,
  i: number
): WorkoutSession | null {
  const owed = session.owedExclusions ?? [];
  if (
    session.swaps[i] === undefined ||
    session.swapScopes?.[i] !== undefined ||
    owed.includes(i)
  ) {
    return null;
  }
  return { ...session, owedExclusions: [...owed, i] };
}

function withoutOwed(session: WorkoutSession, i: number): WorkoutSession {
  const owed = session.owedExclusions;
  return owed?.includes(i) === true
    ? { ...session, owedExclusions: owed.filter((j) => j !== i) }
    : session;
}

/**
 * The plan item behind `i` was REPLACED, so the session's copy takes the id
 * of the item that now exists (T31: `…/replace` soft-deletes the old row and
 * creates a new one with a new id, and the alternatives and candidates routes
 * 404 `plan.item_not_found` on the dead one).
 *
 * `itemId` alone moves. `exerciseId`, `exercise` and every target stay as the
 * session started: today is still being done to the prescription it opened
 * with, and the substitute standing there is `swaps[i]`'s. So the logged sets,
 * `exerciseIdAt`, the summary and the feedback mapper are untouched — and so
 * is `swapIndexFor`, which resolves the deck's `ex=` against `exerciseId`.
 *
 * `null` when the item already carries that id, so a repeat writes nothing.
 */
export function rebaseItem(
  session: WorkoutSession,
  i: number,
  itemId: string
): WorkoutSession | null {
  const item = session.items[i];
  if (item === undefined || item.itemId === itemId) {
    return null;
  }
  return {
    ...session,
    items: session.items.map((it, j) => (j === i ? { ...it, itemId } : it)),
  };
}

/**
 * Today only (T57): the sets of `i` are recorded under `swap.exerciseId`.
 * `null`, or the item's own exercise, puts the original back. An answer given
 * for the swap that stood at `i` goes with it: it was about that exercise.
 */
export function swapToday(
  session: WorkoutSession,
  i: number,
  swap: TodaySwap | null
): WorkoutSession | null {
  const item = session.items[i];
  if (session.ended !== null || item === undefined) {
    return null;
  }
  const swaps = { ...session.swaps };
  if (swap === null || swap.exerciseId === item.exerciseId) {
    if (swaps[i] === undefined) {
      return null;
    }
    delete swaps[i];
  } else {
    if (swaps[i]?.exerciseId === swap.exerciseId) {
      return null;
    }
    swaps[i] = swap;
  }
  let next: WorkoutSession = withoutOwed({ ...session, swaps }, i);
  if (session.swapScopes?.[i] !== undefined) {
    const swapScopes = { ...session.swapScopes };
    delete swapScopes[i];
    next = { ...next, swapScopes };
  }
  return i === session.exIndex ? { ...next, ...suggestionAt(next, i) } : next;
}

/**
 * The end, on the client clock (D20), never before the last set: the server
 * would raise it there anyway, and saying so here keeps the summary's time
 * the one the server stores.
 */
export function finish(
  session: WorkoutSession,
  reason: EndReason,
  now: number
): WorkoutSession | null {
  if (session.ended !== null) {
    return null;
  }
  const lastSet = Math.max(
    session.startedAt,
    ...Object.values(session.logged).flatMap((sets) =>
      sets.map((set) => set.performedAt)
    )
  );
  const endedAt = Math.max(now, lastSet);
  const pauses = closedPauses(session.pauses, endedAt);
  return {
    ...session,
    ended: reason,
    endedAt,
    phase: "done",
    restEndsAt: null,
    ...(pauses === undefined ? {} : { pauses }),
  };
}

// ── D245: the paused clock ──────────────────────────────────────────────────

/** The pauses with an open last one closed at `at` (never before it began). */
function closedPauses(
  pauses: readonly ClockPause[] | undefined,
  at: number
): readonly ClockPause[] | undefined {
  const last = pauses?.at(-1);
  if (pauses === undefined || last === undefined || last.to !== undefined) {
    return pauses;
  }
  return [
    ...pauses.slice(0, -1),
    { from: last.from, to: Math.max(last.from, at) },
  ];
}

/** Whether the clock is paused now: the last pause has not ended. */
export function isPaused(session: Pick<WorkoutSession, "pauses">): boolean {
  const last = session.pauses?.at(-1);
  return last !== undefined && last.to === undefined;
}

/**
 * The paused milliseconds inside `[startedAt, until]`: each pause clipped to
 * that span, an open one running to `until`. What an end chosen in the past
 * reads, as well as the clock.
 */
export function pausedMsWithin(
  session: Pick<WorkoutSession, "pauses" | "startedAt">,
  until: number
): number {
  let total = 0;
  for (const pause of session.pauses ?? []) {
    const from = Math.max(pause.from, session.startedAt);
    const to = Math.min(pause.to ?? until, until);
    total += Math.max(0, to - from);
  }
  return total;
}

/**
 * The trained time on the clock at `now` (or at the end): the span less the
 * pauses inside it. The top bar, the shell's live button and the
 * after-workout numbers all read this, so a paused minute is out of every
 * one of them.
 */
export function activeElapsedMs(
  session: Pick<WorkoutSession, "endedAt" | "pauses" | "startedAt">,
  now: number
): number {
  const until = session.endedAt ?? now;
  return Math.max(
    0,
    until - session.startedAt - pausedMsWithin(session, until)
  );
}

/**
 * What the clock shows at `now`: {@link activeElapsedMs}, standing still at
 * the moment of the pause while paused — so a screen that stops ticking
 * when paused still draws the exact figure.
 */
export function clockMs(
  session: Pick<WorkoutSession, "endedAt" | "pauses" | "startedAt">,
  now: number
): number {
  const last = session.pauses?.at(-1);
  const at = last !== undefined && last.to === undefined ? last.from : now;
  return activeElapsedMs(session, at);
}

/** The `pausedSeconds` an end of the session sends; 0 with no pause. */
export function pausedSecondsAt(
  session: Pick<WorkoutSession, "pauses" | "startedAt">,
  until: number
): number {
  return Math.round(pausedMsWithin(session, until) / 1000);
}

/**
 * Pause the clock. A running rest ends here — the person stepped away, and
 * coming back is not the moment for an alarm — and nothing else moves:
 * the sets, the cursor and the scratch numbers wait. `null` when the
 * workout has ended or is already paused.
 */
export function pauseClock(
  session: WorkoutSession,
  now: number
): WorkoutSession | null {
  if (session.ended !== null || isPaused(session)) {
    return null;
  }
  return {
    ...session,
    pauses: [
      ...(session.pauses ?? []),
      { from: Math.max(now, session.startedAt) },
    ],
    ...(session.phase === "rest"
      ? { phase: "log" as const, restEndsAt: null }
      : {}),
  };
}

/** Run the clock again. `null` when it is not paused. */
export function resumeClock(
  session: WorkoutSession,
  now: number
): WorkoutSession | null {
  if (!isPaused(session)) {
    return null;
  }
  return { ...session, pauses: closedPauses(session.pauses, now) };
}

// ── D209: set kinds, warm-ups, "Preencher abaixo", the live record ─────────

/**
 * The kind sheet's answer for the NEXT set (B1): what it is logged as and
 * on which side. `undefined` leaves a field as it was; `null` resets it to
 * the default (a working set, both sides). Only while logging or resting —
 * the next set is the one the sheet was opened for in both.
 */
export function chooseNextSet(
  session: WorkoutSession,
  choice: {
    readonly kind?: SetKind | null;
    readonly side?: SetSide | null;
  }
): WorkoutSession | null {
  if (session.ended !== null) {
    return null;
  }
  // "Normal" where the default would be a warm-up is said out loud, or it
  // would fall straight back to the planned warm-up.
  const planned = plannedWarmupAt(session, session.exIndex) !== null;
  let draftKind: WorkoutSession["draftKind"] = choice.kind ?? undefined;
  if (choice.kind === null && planned) {
    draftKind = "working";
  }
  const next: WorkoutSession = {
    ...session,
    ...(choice.kind === undefined ? {} : { draftKind }),
    ...(choice.side === undefined
      ? {}
      : { draftSide: choice.side ?? undefined }),
  };
  return next.draftKind === session.draftKind &&
    next.draftSide === session.draftSide
    ? null
    : next;
}

/**
 * "Adicionar aquecimento" (B2): the calculator's sets go in BEFORE the
 * working sets of `i`, so only while nothing of `i` is logged yet — a
 * warm-up after a working set is a set the person logs by hand, as a
 * warm-up, from the kind sheet. A second call replaces the plan; an empty
 * plan takes it away. `null` where nothing would change.
 */
export function planWarmups(
  session: WorkoutSession,
  i: number,
  planned: readonly PlannedWarmup[]
): WorkoutSession | null {
  if (
    session.ended !== null ||
    session.items[i] === undefined ||
    (session.logged[i]?.length ?? 0) > 0
  ) {
    return null;
  }
  const warmups: Record<number, readonly PlannedWarmup[]> = {
    ...session.warmups,
  };
  if (planned.length === 0) {
    if (warmups[i] === undefined) {
      return null;
    }
    delete warmups[i];
  } else {
    warmups[i] = planned;
  }
  const next: WorkoutSession = { ...session, warmups };
  return i === session.exIndex && session.phase === "log"
    ? { ...next, ...cursorAt(next, i) }
    : next;
}

/** What the second dial of a logged set holds: reps, seconds or metres. */
function secondOf(set: LoggedWorkoutSet): number {
  return set.reps ?? set.durationSeconds ?? set.distanceMetres ?? 0;
}

/**
 * "Preencher abaixo" (B4): the set `setId` names copies its load and its
 * second number into every set of its exercise still to be logged. Nothing
 * is logged — the remaining sets are SUGGESTED at these numbers, which is
 * what the dials then show. A warm-up does not fill: copying 40 % into the
 * working sets is never what "fill below" means. `null` where the set is
 * not here, is a warm-up, or nothing of its exercise is left to log.
 */
export function fillBelow(
  session: WorkoutSession,
  setId: string
): WorkoutSession | null {
  const at = locateSet(session, setId);
  if (at === null || at.set.kind === "warmup" || session.ended !== null) {
    return null;
  }
  const i = at.exIndex;
  const live = liveOf(session);
  if ((session.logged[i]?.length ?? 0) >= (live.sets[i] ?? 0)) {
    return null;
  }
  const next: WorkoutSession = {
    ...session,
    fill: {
      ...session.fill,
      [i]: { load: at.set.load, second: secondOf(at.set) },
    },
  };
  return i === session.exIndex && plannedWarmupAt(next, i) === null
    ? { ...next, ...suggestionAt(next, i) }
    : next;
}

/** Every set logged today, any exercise, in no particular order. */
export function todaysSets(
  session: Pick<WorkoutSession, "logged">
): readonly LoggedWorkoutSet[] {
  return Object.values(session.logged).flat();
}

/** "Sim" to a plausibility question (`D244`): asked once per value. */
export function confirmValues(
  session: WorkoutSession,
  keys: readonly string[]
): WorkoutSession | null {
  const held = session.confirmed ?? [];
  const added = keys.filter((key) => !held.includes(key));
  return added.length === 0
    ? null
    : { ...session, confirmed: [...held, ...added] };
}

/** The server's cut, once the start has synced (or the local one stands). */
export function settleAdjustments(
  session: WorkoutSession,
  sessionId: string,
  adjustments: readonly SessionAdjustment[] | null
): WorkoutSession | null {
  if (session.sessionId !== sessionId || session.adjustmentsSettled) {
    return null;
  }
  return {
    ...session,
    adjustments: adjustments ?? session.adjustments,
    adjustmentsSettled: true,
  };
}

// ── D254: an exercise added today, and the blank workout ───────────────────

/** The prefix of a line added during the workout: no plan row has it. */
export const ADDED_ITEM_PREFIX = "added-";

/** The `sessionId` an added line carries — no plan day's, on purpose. */
export const ADDED_SESSION_ID = "added";

/** How many exercises one workout takes on top of its own. */
export const MAX_ADDED_EXERCISES = 12;

/** Whether the line at hand was added during the workout (`D254`). */
export function isAddedItem(item: Pick<PlanItemView, "itemId">): boolean {
  return item.itemId.startsWith(ADDED_ITEM_PREFIX);
}

/** The indexes of the lines added today, in order. */
export function addedIndexes(
  session: Pick<WorkoutSession, "items">
): readonly number[] {
  return session.items.flatMap((item, i) => (isAddedItem(item) ? [i] : []));
}

/**
 * Nothing is left to log, and the session has not ended: a blank workout
 * with no exercise yet, or with every set of its exercises done (`D254`).
 * The screen then offers the next exercise and the end.
 */
export function isWaiting(
  session: Pick<
    WorkoutSession,
    | "adjustments"
    | "ended"
    | "exIndex"
    | "extra"
    | "items"
    | "logged"
    | "swaps"
    | "warmups"
  >
): boolean {
  if (session.ended !== null) {
    return false;
  }
  return (
    session.items.length === 0 ||
    nextOpenExercise(session, liveOf(session), session.exIndex) < 0
  );
}

/**
 * An exercise for today only (`D254`): `item` goes at the END of the day,
 * outside any superset (`D246` — it carries no `supersetId`, and the run
 * rule groups only lines that share one). The cursor moves to it when the
 * exercise under it has nothing left, so a blank workout, or a day logged to
 * its end, goes straight on; otherwise it waits its turn. `null` when the
 * session ended, the exercise is already part of today — its own line or a
 * swap's — or the day already took {@link MAX_ADDED_EXERCISES}.
 */
export function addExercise(
  session: WorkoutSession,
  item: PlanItemView
): WorkoutSession | null {
  if (session.ended !== null) {
    return null;
  }
  const today = new Set([
    ...session.items.map((one) => one.exerciseId),
    ...Object.values(session.swaps).map((swap) => swap.exerciseId),
  ]);
  if (
    today.has(item.exerciseId) ||
    addedIndexes(session).length >= MAX_ADDED_EXERCISES
  ) {
    return null;
  }
  const i = session.items.length;
  const { supersetId: _group, ...line } = item;
  const next: WorkoutSession = {
    ...session,
    items: [...session.items, { ...line, position: i }],
  };
  const waiting =
    session.items.length === 0 || !isOpen(next, liveOf(next), session.exIndex);
  if (!waiting) {
    return next;
  }
  return session.phase === "rest"
    ? { ...next, ...cursorAt(next, i), phase: "rest" }
    : { ...next, ...cursorAt(next, i), phase: "log", restEndsAt: null };
}

/**
 * The add's "Desfazer": the line `itemId` names comes off — only an added
 * one, only the LAST line (every map of the session keys on the index, and
 * no other index moves this way), and only while nothing of it is logged.
 * `null` otherwise.
 */
export function withdrawAdded(
  session: WorkoutSession,
  itemId: string
): WorkoutSession | null {
  const i = session.items.length - 1;
  const item = session.items[i];
  if (
    session.ended !== null ||
    item === undefined ||
    item.itemId !== itemId ||
    !isAddedItem(item) ||
    (session.logged[i]?.length ?? 0) > 0
  ) {
    return null;
  }
  const next: WorkoutSession = {
    ...session,
    deferred: session.deferred?.filter((j) => j !== i),
    extra: withoutIndex(session.extra, i),
    fill: withoutIndex(session.fill, i),
    items: session.items.slice(0, -1),
    logged: withoutIndex(session.logged, i),
    swaps: withoutIndex(session.swaps, i),
    warmups: withoutIndex(session.warmups, i),
  };
  if (session.exIndex !== i) {
    return next;
  }
  const back = Math.max(0, i - 1);
  const open = nextOpenExercise(next, liveOf(next), back);
  return { ...next, ...cursorAt(next, open < 0 ? back : open) };
}

/** `map` without the key `i`. */
function withoutIndex<T>(
  map: Readonly<Record<number, T>> | undefined,
  i: number
): Record<number, T> {
  const copy: Record<number, T> = { ...map };
  delete copy[i];
  return copy;
}
